import { ENV } from "@/constants";
import {
  GOOGLE_PLACES_API_KEY,
  MAP_LIBRARIES,
} from "@/config/google";
import { BrandColors, Colors } from "@/constants/theme";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useJsApiLoader } from "@react-google-maps/api";
import { useQuery } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { ThemedText } from "../themed-text";

interface Place {
  address: string;
  city: string;
  latitude: number;
  longitude: number;
}

interface GooglePlacesInputProps {
  isPickoff?: boolean;
  placeholder: string;
  isAbsolute?: boolean;
  isPredefined?: boolean;
  onPlaceSelected: (place: Place) => void;
}

const PREDEFINED_PLACES_KEY = "@predefined_places";

let predefinedPlacesCache: any[] = [];
const predefinedPlacesListeners = new Set<() => void>();
let predefinedPlacesLoadStarted = false;

function subscribePredefinedPlaces(onStoreChange: () => void) {
  predefinedPlacesListeners.add(onStoreChange);
  if (!predefinedPlacesLoadStarted) {
    predefinedPlacesLoadStarted = true;
    AsyncStorage.getItem(PREDEFINED_PLACES_KEY).then((res) => {
      if (!res) {
        return;
      }
      try {
        predefinedPlacesCache = JSON.parse(res);
        predefinedPlacesListeners.forEach((listener) => listener());
      } catch (e) {
        console.error("Failed to parse predefined places", e);
      }
    });
  }
  return () => {
    predefinedPlacesListeners.delete(onStoreChange);
  };
}

function getPredefinedPlacesSnapshot() {
  return predefinedPlacesCache;
}

function setPredefinedPlacesCache(places: any[]) {
  predefinedPlacesCache = places;
  predefinedPlacesListeners.forEach((listener) => listener());
}

const GooglePlacesInput = forwardRef<any, GooglePlacesInputProps>(
  (
    {
      isPickoff = false,
      placeholder,
      isAbsolute = false,
      isPredefined = true,
      onPlaceSelected,
    },
    ref,
  ) => {
    const colorScheme = useColorScheme();
    const theme = Colors[colorScheme ?? "light"];
    const storedPlaces = useSyncExternalStore(
      subscribePredefinedPlaces,
      getPredefinedPlacesSnapshot,
      getPredefinedPlacesSnapshot,
    );
    const predefinedPlaces = isPredefined ? storedPlaces : [];
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [localValue, setLocalValue] = useState("");
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [status, setStatus] = useState("");
    const [sessionToken, setSessionToken] = useState<any>(null);
    const placesLib = useRef<any>(null);
    const debounceTimer = useRef<any>(null);

    // Register (and other non-map screens) never mount a map, so the Maps JS
    // API is not on window yet. Load it here the same way maps do.
    const { isLoaded: isMapsScriptLoaded } = useJsApiLoader({
      id: "google-map-script",
      googleMapsApiKey: GOOGLE_PLACES_API_KEY,
      libraries: MAP_LIBRARIES as never[],
    });

    const { data: placesApi } = useQuery({
      queryKey: ["google-places-lib"],
      enabled: isMapsScriptLoaded,
      staleTime: Infinity,
      retry: 0,
      queryFn: async () => {
        // @ts-ignore
        const lib = await google.maps.importLibrary("places");
        // @ts-ignore
        return { lib, token: new lib.AutocompleteSessionToken() };
      },
    });

    useEffect(() => {
      if (!placesApi) {
        return;
      }
      placesLib.current = placesApi.lib;
      setSessionToken((current: any) => current ?? placesApi.token);
    }, [placesApi]);

    const fetchSuggestions = async (input: string) => {
      if (!input || !placesLib.current) {
        setSuggestions([]);
        setStatus("");
        return;
      }

      try {
        const request = {
          input,
          sessionToken,
          includedRegionCodes: ENV === "production" ? ["gb"] : undefined,
          language: "en",
        };

        const { suggestions: results } =
          await placesLib.current.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request,
          );

        if (results && results.length > 0) {
          setSuggestions(
            results.map((s: any) => ({
              place_id: s.placePrediction.placeId,
              description: s.placePrediction.text.text,
              rawSuggestion: s,
            })),
          );
          setStatus("OK");
        } else {
          setSuggestions([]);
          setStatus("ZERO_RESULTS");
        }
      } catch (e) {
        console.error("Autocomplete error", e);
        setStatus("ERROR");
      }
    };

    const setValue = (val: string, shouldFetch = true) => {
      setLocalValue(val);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (shouldFetch) {
        debounceTimer.current = setTimeout(() => fetchSuggestions(val), 400);
      }
    };

    const clearSuggestions = () => {
      setSuggestions([]);
      setStatus("");
    };

    const value = localValue;
    const data = suggestions;

    const savePlaceToRecent = async (
      description: string,
      latLng: { lat: number; lng: number },
      city: string,
    ) => {
      if (!isPredefined) return;
      try {
        const selectedPlace = {
          description,
          geometry: { location: latLng },
          city,
        };
        const currentRes = await AsyncStorage.getItem(PREDEFINED_PLACES_KEY);
        let places = currentRes ? JSON.parse(currentRes) : [];
        places = places.filter(
          (p: any) => p.description !== selectedPlace.description,
        );
        places.unshift(selectedPlace);
        places = places.slice(0, 5);
        await AsyncStorage.setItem(
          PREDEFINED_PLACES_KEY,
          JSON.stringify(places),
        );
        setPredefinedPlacesCache(places);
      } catch (e) {
        console.error("Failed to save place", e);
      }
    };

    const handleSelect = async (suggestion: any) => {
      const { description, rawSuggestion } = suggestion;
      setValue(description, false);
      clearSuggestions();
      setShowSuggestions(false);

      try {
        const place = rawSuggestion.placePrediction.toPlace();
        await place.fetchFields({
          fields: ["location", "addressComponents", "formattedAddress"],
          sessionToken: sessionToken,
        });

        const lat = place.location?.lat() || 0;
        const lng = place.location?.lng() || 0;
        const city =
          place.addressComponents?.find((component: any) =>
            component.types.includes("locality"),
          )?.longText || "";

        onPlaceSelected({
          address: place.formattedAddress || description,
          city,
          latitude: lat,
          longitude: lng,
        });

        // Conclude session and create new token for next session
        if (placesLib.current) {
          setSessionToken(new placesLib.current.AutocompleteSessionToken());
        }

        if (isPredefined) {
          savePlaceToRecent(description, { lat, lng }, city);
        }
      } catch (error) {
        console.error("Error fetching place details:", error);
      }
    };

    const handlePredefinedSelect = (place: any) => {
      const { description } = place;
      const { lat, lng } = place.geometry.location;
      const city = place.city || "";

      setValue(description, false);
      clearSuggestions();
      setShowSuggestions(false);

      onPlaceSelected({
        address: description,
        city,
        latitude: lat,
        longitude: lng,
      });
    };

    const inputRef = React.useRef<TextInput>(null);

    useImperativeHandle(ref, () => ({
      setAddressText: (text: string) => setValue(text, false),
      clear: () => {
        setValue("");
        clearSuggestions();
      },
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
    }));

    return (
      <View style={styles.container}>
        <View
          style={[
            styles.textInputContainer,
            { backgroundColor: theme.background, borderColor: theme.border },
          ]}
        >
          <Ionicons
            name={isPickoff ? "location" : "flag"}
            size={20}
            color={BrandColors.mediumGray}
          />
          <TextInput
            ref={inputRef}
            placeholder={placeholder}
            placeholderTextColor={BrandColors.mediumGray}
            value={value}
            onChangeText={(text) => {
              setValue(text);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            style={[styles.textInput, { color: theme.text }]}
          />
        </View>

        {showSuggestions && (
          <ScrollView
            style={[
              styles.listView,
              { backgroundColor: theme.background, borderColor: theme.border },
              isAbsolute && styles.absoluteListView,
            ]}
            keyboardShouldPersistTaps="handled"
          >
            {status === "OK" &&
              data.map((suggestion) => (
                <TouchableOpacity
                  key={suggestion.place_id}
                  onPress={() => handleSelect(suggestion)}
                  style={[styles.row, { borderBottomColor: theme.border }]}
                >
                  <ThemedText
                    style={[styles.description, { color: theme.text }]}
                  >
                    {suggestion.description}
                  </ThemedText>
                </TouchableOpacity>
              ))}

            {isPredefined &&
              predefinedPlaces.length > 0 &&
              (status !== "OK" || data.length === 0) &&
              predefinedPlaces.map((place, index) => (
                <TouchableOpacity
                  key={`predefined-${index}`}
                  onPress={() => handlePredefinedSelect(place)}
                  style={[styles.row, { borderBottomColor: theme.border }]}
                >
                  <ThemedText
                    style={[styles.description, { color: BrandColors.primary }]}
                  >
                    {place.description}
                  </ThemedText>
                </TouchableOpacity>
              ))}
          </ScrollView>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
    zIndex: 1000,
    elevation: 5,
  },
  textInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 48,
  },
  textInput: {
    flex: 1,
    height: "100%",
    marginLeft: 8,
    fontSize: 16,
    fontFamily: "PlusJakartaSans",
    outlineStyle: "none",
  } as any,
  listView: {
    marginTop: 4,
    borderWidth: 1,
    borderRadius: 12,
    maxHeight: 200,
    zIndex: 1001,
  },
  absoluteListView: {
    position: "absolute",
    top: 160,
    left: 0,
    right: 0,
  },
  row: {
    padding: 13,
    borderBottomWidth: 0.5,
  },
  description: {
    fontSize: 14,
    fontFamily: "PlusJakartaSans",
  },
});

export default GooglePlacesInput;
